// Internal combat types: optional extension fields carried inside the public contract objects
// (extra properties on ProjectileSpec / chainLightning opts are invisible to other modules but
// let combat pass richer data between its own subsystems), plus safe accessors for systems
// that may not exist yet (stubs) or may be partially mocked.

import type {
  EnemyApi,
  FxApi,
  GameContext,
  ProjectileApi,
  ProjectileSpec,
  StatusApi,
  Vec,
  ZoneSpec,
} from '../../core/contracts';
import type { DamageInfo, StatBlock, StatusApplication, Tag, TrailKind, WeaponId } from '../../core/types';
import { BASE_STATS } from '../../data/mechs';

/** ProjectileSpec plus combat-internal fields. */
export interface ProjectileSpecEx extends ProjectileSpec {
  /** Item-effect projectiles: no on-hit item triggers (prevents recursion). */
  noTrigger?: boolean;
  /** Damage label for stats / debugging. */
  label?: string;
  /**
   * Player shots: on the first step also test enemies this many px behind the spawn point,
   * so a target standing between the shoulder and the muzzle is still hit (point-blank).
   */
  backtrace?: number;
  /** Override the impact FX kind. */
  impact?: ImpactKind;
  /** Micro-missiles: launch at this fraction of `speed`, reaching full speed after 0.3 s. */
  launchSpeedFrac?: number;
  /** Do not explode / burst into a field when the lifetime runs out (just fade). */
  quietExpire?: boolean;
}

/** DamageInfo plus combat-internal fields (carried through hitEnemy into kill handling). */
export interface DamageInfoEx extends DamageInfo {
  /** Fragmenting mod: kills explode (radius in px before stats.explosionRadius, damage × the killing hit). */
  explodeOnKill?: { radius: number; damage: number };
}

/** ZoneSpec plus combat-internal fields. */
export interface ZoneSpecEx extends ZoneSpec {
  /** Rect zones: rotation in radians around (x, y) (dash trails along diagonal paths). */
  angle?: number;
  /** Trail visual family (zones created through trail()). */
  trailKind?: TrailKind;
}

export type ImpactKind = 'bullet' | 'heavy' | 'energy' | 'shield' | 'pellet';

/** chainLightning options plus combat-internal fields. */
export interface ChainOptsEx {
  count: number;
  range: number;
  damage: number;
  tags?: Tag[];
  color?: number;
  exclude?: Set<EnemyApi>;
  secondary?: boolean;
  /** Damage multiplier per hop (WeaponDef.chain.falloff). Default 1. */
  falloff?: number;
  noTrigger?: boolean;
  weaponId?: WeaponId;
  /** Extra statuses on every strike (weapon mods), on top of the shock stack. */
  statuses?: StatusApplication[];
  label?: string;
  /** Visual width of the bolt. */
  width?: number;
}

/** explode() options plus combat-internal fields. */
export interface ExplodeOptsEx {
  hurtsPlayer?: boolean;
  fx?: boolean;
  color?: number;
  exclude?: Set<EnemyApi>;
  /** Exact damage to the player when hurtsPlayer (default: falloff-scaled amount; 5 for player-made blasts). */
  playerDamage?: number;
  /** Override camera shake intensity (0 disables). */
  shake?: number;
}

/** Status values captured right before a killing blow (enemies may clear their maps on death). */
export interface DeathSnapshot {
  burnStacks: number;
  burnPotency: number;
  burnRemaining: number;
  overheated: boolean;
}

export interface StatusSystem extends StatusApi {
  /** WILDFIRE: a burning enemy died — spread its burn. */
  onKilled(target: EnemyApi, snap: DeathSnapshot): void;
}

export interface ProjectileSystem extends ProjectileApi {
  /** Bend enemy projectiles toward a point (gravity fields). */
  nudgeEnemyShots(x: number, y: number, radius: number, strength: number, dt: number): void;
}

/**
 * FxApi wrapper that never throws: the FX module may still be a stub, or one effect may be
 * broken, and a missing spark must never take a projectile update down with it.
 * Each failing method is reported once.
 */
export function createSafeFx(ctx: GameContext): FxApi {
  const cache = new Map<PropertyKey, (...args: unknown[]) => unknown>();
  const warned = new Set<PropertyKey>();
  return new Proxy({} as FxApi, {
    get(_t, key) {
      let fn = cache.get(key);
      if (!fn) {
        fn = (...args: unknown[]) => {
          try {
            const target = ctx.fx as unknown as Record<PropertyKey, unknown> | undefined;
            const f = target?.[key];
            return typeof f === 'function' ? (f as (...a: unknown[]) => unknown).apply(target, args) : undefined;
          } catch (err) {
            if (!warned.has(key)) {
              warned.add(key);
              console.warn(`[combat] fx.${String(key)} failed`, err);
            }
            return undefined;
          }
        };
        cache.set(key, fn);
      }
      return fn;
    },
  });
}

/** Player stats, or mech base stats when the player does not exist yet. */
export function playerStats(ctx: GameContext): Readonly<StatBlock> {
  try {
    return ctx.player?.stats ?? BASE_STATS;
  } catch {
    return BASE_STATS;
  }
}

/** effects.hasFlag that never throws (effects may be missing in tools / previews). */
export function flag(ctx: GameContext, name: string): boolean {
  try {
    return ctx.effects?.hasFlag(name) ?? false;
  } catch {
    return false;
  }
}

/** The live player, or null while it does not exist / is dead-and-gone. */
export function livePlayer(ctx: GameContext): GameContext['player'] | null {
  try {
    return ctx.player ?? null;
  } catch {
    return null;
  }
}

/** Live enemy list ([] while the enemy system is unavailable). */
export function enemyList(ctx: GameContext): readonly EnemyApi[] {
  try {
    return ctx.enemies?.list ?? [];
  } catch {
    return [];
  }
}

/** Valid auto-target: alive, active, not breakable scenery. */
export function targetable(e: EnemyApi): boolean {
  return e.alive && !e.spawning && e.kind !== 'prop';
}

export function withTag(tags: readonly Tag[] | undefined, tag: Tag): Tag[] {
  const out = tags ? tags.slice() : [];
  if (!out.includes(tag)) out.push(tag);
  return out;
}

export function vec(x: number, y: number): Vec {
  return { x, y };
}

/** Destroy a game object that Phaser may already have destroyed (scene shutdown). */
export function destroyObj(o: { destroy(): void } | null | undefined): void {
  if (!o) return;
  try {
    o.destroy();
  } catch {
    // already gone
  }
}
